import { useReactFlow } from '@xyflow/react';
import { useCatalogue } from '../EcaPluginCatalogueContext.jsx';
import { NODE_TYPE_DOCS, getFieldDoc } from '../mock/nodeTypes.js';
import { useExecutionMock } from '../mock/ExecutionMockContext.jsx';
import { useVariables, VAR_PALETTE } from '../EcaVariablesContext.jsx';

/**
 * Right sidebar config panel.
 * Shows node type, plugin info, editable fields with inline doc strings,
 * and for gateway nodes: branch state simulation controls.
 * For variable nodes: label, default value, color, duplicate/delete actions.
 */
export const NodeConfigPanel = ({ node, onClose }) => {
  const { updateNodeData, deleteElements } = useReactFlow();
  const { getSession, markBranchDone, resetGateway, setResetAfterFire } = useExecutionMock();
  const { variables, updateVariable, duplicateVarRef, deleteVarRef, deleteVariable } = useVariables();
  const { getPluginDef } = useCatalogue();

  if (!node) return null;

  // Variable nodes get their own dedicated panel
  if (node.type === 'variable') {
    return (
      <VariableConfigPanel
        node={node}
        onClose={onClose}
        variables={variables}
        updateVariable={updateVariable}
        onDuplicateRef={() => duplicateVarRef(node.id)}
        onDeleteRef={() => { deleteVarRef(node.id); onClose(); }}
        onDeleteVariable={(varId) => { deleteVariable(varId); onClose(); }}
      />
    );
  }

  const typeDocs = NODE_TYPE_DOCS[node.type] ?? {};
  const pluginCategory = { event: 'events', action: 'actions', condition: 'conditions', gateway: 'gateways' }[node.type];
  const pluginDef = pluginCategory ? getPluginDef(pluginCategory, node.data.plugin) : null;
  const configSchema = pluginDef?.configSchema ?? [];

  const updateConfig = (key, value) =>
    updateNodeData(node.id, { configuration: { ...node.data.configuration, [key]: value } });

  const updateLabel = (value) => updateNodeData(node.id, { label: value });

  const handleDelete = () => {
    deleteElements({ nodes: [{ id: node.id }] });
    onClose();
  };

  const classBadgeColors = {
    standard: 'bg-blue-50 text-blue-600 border-blue-200',
    flow: 'bg-rose-50 text-rose-600 border-rose-200',
  };

  return (
    <div className="w-80 bg-white border-l border-gray-200 shadow-xl flex flex-col h-full overflow-hidden">
      {/* Panel header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center gap-2">
          <span
            className={`text-xs font-semibold px-2 py-0.5 rounded border uppercase tracking-wide ${
              classBadgeColors[typeDocs.class] ?? 'bg-gray-50 text-gray-500 border-gray-200'
            }`}
          >
            {typeDocs.class ?? node.type}
          </span>
          <span className="text-sm font-semibold text-gray-800">{typeDocs.label ?? node.type}</span>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">
          ×
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Plugin description — single source: pluginDef.description from ecaMock.js */}
        {pluginDef?.description && (
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
            <p className="text-xs text-gray-500 leading-relaxed">{pluginDef.description}</p>
          </div>
        )}

        {/* Plugin info */}
        {node.data.plugin && (
          <div className="px-4 py-2 border-b border-gray-100">
            <span className="text-xs text-gray-400">Plugin</span>
            <p className="text-xs font-mono text-gray-700 mt-0.5">{node.data.plugin}</p>
          </div>
        )}

        <div className="px-4 py-3 space-y-4">
          {/* Label field */}
          <ConfigField
            label="Label"
            doc={typeDocs.fields?.label?.doc}
          >
            <input
              type="text"
              value={node.data.label ?? ''}
              onChange={(e) => updateLabel(e.target.value)}
              className="input"
            />
          </ConfigField>

          {/* Config fields from schema */}
          {configSchema.map((field) => (
            <ConfigField
              key={field.key}
              label={field.label}
              doc={field.description || getFieldDoc(node.type, field.key)}
              wirable={field.wirable}
            >
              {field.type === 'boolean' ? (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!node.data.configuration?.[field.key]}
                    onChange={(e) => updateConfig(field.key, e.target.checked)}
                    className="w-4 h-4 accent-iris-600 rounded"
                  />
                  <span className="text-sm text-gray-600">
                    {node.data.configuration?.[field.key] ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
              ) : field.type === 'select' ? (
                <select
                  value={node.data.configuration?.[field.key] ?? ''}
                  onChange={(e) => updateConfig(field.key, e.target.value)}
                  className="select"
                >
                  {field.options?.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={node.data.configuration?.[field.key] ?? ''}
                  placeholder={field.placeholder}
                  onChange={(e) => updateConfig(field.key, e.target.value)}
                  className="input"
                />
              )}
            </ConfigField>
          ))}

          {/* Gateway: execution simulation */}
          {node.type === 'gateway' && <GatewaySimPanel nodeId={node.id} branches={node.data.branches ?? []} data={node.data} updateNodeData={updateNodeData} getSession={getSession} markBranchDone={markBranchDone} resetGateway={resetGateway} setResetAfterFire={setResetAfterFire} />}
        </div>
      </div>

      {/* Delete button */}
      <div className="px-4 py-3 border-t border-gray-100">
        <button
          onClick={handleDelete}
          className="btn btn-danger btn-block"
        >
          Delete node
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const ConfigField = ({ label, doc, wirable, children }) => (
  <div className="space-y-1.5">
    <div className="flex items-center gap-2">
      <span className="text-xs font-semibold text-gray-700">{label}</span>
      {wirable && (
        <span
          className="text-xs bg-green-50 text-green-600 border border-green-200 px-1.5 py-0 rounded"
          title="This field can receive a data wire from another node"
        >
          wirable
        </span>
      )}
    </div>
    {children}
    {doc && <p className="text-xs text-gray-400 leading-relaxed">{doc}</p>}
  </div>
);

// ---------------------------------------------------------------------------
// Variable node config panel
// ---------------------------------------------------------------------------

const VariableConfigPanel = ({ node, onClose, variables, updateVariable, onDuplicateRef, onDeleteRef, onDeleteVariable }) => {
  const varId  = node.data?.varId;
  const varMeta = variables[varId] ?? { label: '', color: '#8B5CF6', defaultValue: '' };

  if (!varId) return null;

  return (
    <div className="w-80 bg-white border-l border-gray-200 shadow-xl flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center gap-2">
          <span
            className="w-3 h-3 rounded-full flex-shrink-0"
            style={{ background: varMeta.color ?? '#8B5CF6' }}
          />
          <span className="text-xs font-semibold px-2 py-0.5 rounded border uppercase tracking-wide bg-iris-50 text-iris-600 border-iris-200">
            variable
          </span>
          <span className="text-sm font-semibold text-gray-800">
            {varMeta.label || <span className="text-gray-400 font-normal italic">unlabeled</span>}
          </span>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">
          ×
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Description */}
        <div className="px-4 py-3 bg-iris-50 border-b border-iris-100">
          <p className="text-xs text-iris-700 leading-relaxed">
            A variable is a named token shared across multiple nodes.
            Connect its <strong>TOP</strong> handle from a node output to receive a value,
            and its <strong>BOTTOM</strong> handle into a node input to provide it.
          </p>
        </div>

        {/* Token ID (read-only) */}
        <div className="px-4 py-2 border-b border-gray-100">
          <span className="text-xs text-gray-400">Token ID (auto)</span>
          <p className="text-xs font-mono text-gray-600 mt-0.5">{varId}</p>
        </div>

        <div className="px-4 py-3 space-y-4">
          {/* Label */}
          <ConfigField label="Label" doc="Display label (cosmetic, does not affect the token ID).">
            <input
              type="text"
              value={varMeta.label ?? ''}
              placeholder="e.g. Current task"
              onChange={(e) => updateVariable(varId, { label: e.target.value })}
              className="input"
            />
          </ConfigField>

          {/* Default value */}
          <ConfigField
            label="Default value"
            doc="Injected as a synthetic eca_token_set_value at the start of each event chain, only if no setter node is connected."
          >
            <input
              type="text"
              value={varMeta.defaultValue ?? ''}
              placeholder="e.g. [entity:title] or static text"
              onChange={(e) => updateVariable(varId, { defaultValue: e.target.value })}
              className="input"
            />
          </ConfigField>

          {/* Color swatches */}
          <ConfigField label="Color" doc="Visual color for this variable's dot and border.">
            <div className="flex gap-1.5 flex-wrap">
              {VAR_PALETTE.map((color) => (
                <button
                  key={color}
                  onClick={() => updateVariable(varId, { color })}
                  className="w-6 h-6 rounded-full transition-transform hover:scale-110 focus:outline-none"
                  style={{
                    background: color,
                    boxShadow: varMeta.color === color
                      ? `0 0 0 2px white, 0 0 0 4px ${color}`
                      : 'none',
                  }}
                  title={color}
                />
              ))}
            </div>
          </ConfigField>
        </div>
      </div>

      {/* Actions */}
      <div className="px-4 py-3 border-t border-gray-100 space-y-2">
        <button
          onClick={onDuplicateRef}
          className="btn btn-ghost btn-block"
        >
          Duplicate reference
        </button>
        <button
          onClick={onDeleteRef}
          className="btn btn-danger btn-block"
        >
          Delete this reference
        </button>
        <button
          onClick={() => onDeleteVariable(varId)}
          className="btn btn-danger btn-block"
        >
          Delete variable (all references)
        </button>
      </div>
    </div>
  );
};

const GatewaySimPanel = ({
  nodeId, branches, data, updateNodeData,
  getSession, markBranchDone, resetGateway, setResetAfterFire,
}) => {
  const session = getSession(nodeId);
  const resetAfterFire = !!data.configuration?.reset_after_fire;

  const toggleResetAfterFire = (val) => {
    updateNodeData(nodeId, { configuration: { ...data.configuration, reset_after_fire: val } });
    setResetAfterFire(nodeId, val);
  };

  return (
    <div className="space-y-3">
      {/* reset_after_fire toggle */}
      <ConfigField
        label="Reset after fire"
        doc={NODE_TYPE_DOCS.gateway.fields.reset_after_fire.doc}
      >
        <div className="flex gap-2">
          <button
            onClick={() => toggleResetAfterFire(false)}
            className={`flex-1 text-xs py-1 rounded border transition-colors ${
              !resetAfterFire
                ? 'bg-gray-700 text-white border-gray-700'
                : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
            }`}
          >
            OFF — one-shot
          </button>
          <button
            onClick={() => toggleResetAfterFire(true)}
            className={`flex-1 text-xs py-1 rounded border transition-colors ${
              resetAfterFire
                ? 'bg-amber-500 text-white border-amber-500'
                : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
            }`}
          >
            ON — repeatable
          </button>
        </div>
      </ConfigField>

      {/* Branch simulation */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-gray-700">Branch simulation</span>
          {session?.fired && (
            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded border border-green-300 font-semibold">
              FIRED ✓
            </span>
          )}
        </div>
        <p className="text-xs text-gray-400">
          Simulate backend branch completion. Each branch notifies the gateway when it finishes.
        </p>

        {session && (
          <div className="space-y-1.5 mt-2">
            {branches.map((branchId) => {
              const state = session.branches?.[branchId] ?? 'pending';
              return (
                <div
                  key={branchId}
                  className="flex items-center gap-2 bg-gray-50 rounded border border-gray-100 px-2 py-1.5"
                >
                  <span
                    className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                      state === 'done'
                        ? 'bg-green-100 text-green-600'
                        : 'bg-gray-200 text-gray-400'
                    }`}
                  >
                    {state === 'done' ? '✓' : '○'}
                  </span>
                  <span className="text-xs font-mono text-gray-600 flex-1 truncate">{branchId}</span>
                  {state !== 'done' ? (
                    <button
                      onClick={() => markBranchDone(nodeId, branchId)}
                      className="text-xs bg-blue-50 text-blue-600 border border-blue-200 px-2 py-0.5 rounded hover:bg-blue-100 transition-colors"
                    >
                      Mark done
                    </button>
                  ) : (
                    <span className="text-xs text-green-500">done</span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <button
          onClick={() => resetGateway(nodeId)}
          className="btn btn-secondary btn-sm btn-block mt-1"
        >
          Reset all branches
        </button>
      </div>

      {/* Session info */}
      {session && (
        <div className="text-xs text-gray-300 font-mono break-all">
          {session.sessionId}
        </div>
      )}
    </div>
  );
};
